import { useRef, type KeyboardEvent } from 'react';

import { AmountInput } from '../AmountInput';
import { SearchSelect } from '../SearchSelect';

export interface LineRow {
  id: string;
  description: string;
  qty: string; // raw numeric string (AmountInput contract)
  unitPrice: string;
  expenseCode: string;
}

export interface LineItemsGridProps {
  lines: LineRow[];
  onChange: (lines: LineRow[]) => void;
  expenseOptions: { value: string; label: string }[];
  subtotal: number;
  vatAmount: number;
  total: number;
  readOnly?: boolean;
}

const MONEY_TOL = 0.01;
// Editable cells per row (description + qty + unitPrice + expenseCode). The
// delete button is not a `data-cell`, so ArrowUp/ArrowDown stride by this count
// to land on the same column of the adjacent row.
const CELLS_PER_ROW = 4;
const num = (s: string) => {
  const n = Number(s);
  return isFinite(n) ? n : 0;
};
const money = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function LineItemsGrid({
  lines,
  onChange,
  expenseOptions,
  subtotal,
  vatAmount,
  total,
  readOnly,
}: LineItemsGridProps) {
  const gridRef = useRef<HTMLTableElement>(null);

  const lineSum = lines.reduce((s, l) => s + num(l.qty) * num(l.unitPrice), 0);
  const computedVat = +(subtotal * 0.07).toFixed(2);
  const sumBad = Math.abs(lineSum - subtotal) >= MONEY_TOL;
  const vatBad = Math.abs(vatAmount - computedVat) >= MONEY_TOL;
  const totalBad = Math.abs(subtotal + vatAmount - total) >= MONEY_TOL;

  const labelFor = (code: string) => expenseOptions.find((o) => o.value === code)?.label ?? code;

  function patch(i: number, p: Partial<LineRow>) {
    onChange(lines.map((l, idx) => (idx === i ? { ...l, ...p } : l)));
  }
  function addRow() {
    onChange([
      ...lines,
      {
        id: `n${Date.now()}-${lines.length}`,
        description: '',
        qty: '1',
        unitPrice: '0',
        expenseCode: '',
      },
    ]);
  }
  function removeRow(i: number) {
    onChange(lines.filter((_, idx) => idx !== i));
  }

  // Roving tabindex: arrow keys move focus between cells within the grid. The
  // `data-cell` marker lives on the real focusable <input> of each editable
  // cell, so `document.activeElement` is always one of `cells`.
  function onGridKeyDown(e: KeyboardEvent<HTMLTableElement>) {
    // When an editable cell is itself a combobox with an OPEN listbox (the
    // expenseCode SearchSelect), ArrowUp/ArrowDown belong to the dropdown — it
    // moves the highlighted option. SearchSelect handles them and only calls
    // preventDefault, so they still bubble here. Yield those keys to the open
    // dropdown rather than also yanking roving focus to the adjacent row.
    const ae = document.activeElement;
    if (
      (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
      ae?.getAttribute('role') === 'combobox' &&
      ae.getAttribute('aria-expanded') === 'true'
    ) {
      return;
    }
    const cells = Array.from(gridRef.current?.querySelectorAll<HTMLElement>('[data-cell]') ?? []);
    const cur = cells.indexOf(ae as HTMLElement);
    if (cur < 0) return;
    let next = -1;
    if (e.key === 'ArrowRight') next = cur + 1;
    else if (e.key === 'ArrowLeft') next = cur - 1;
    else if (e.key === 'ArrowDown') next = cur + CELLS_PER_ROW;
    else if (e.key === 'ArrowUp') next = cur - CELLS_PER_ROW;
    if (next >= 0 && next < cells.length) {
      e.preventDefault();
      cells[next]?.focus();
    }
  }

  return (
    <div>
      <table
        ref={gridRef}
        role="grid"
        tabIndex={0}
        className="li-grid"
        aria-label="รายการสินค้า"
        onKeyDown={onGridKeyDown}
      >
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>รายการ</th>
            <th style={{ width: 70 }}>จำนวน</th>
            <th style={{ width: 110 }}>ราคา/หน่วย</th>
            <th style={{ width: 160 }}>รหัสบัญชี</th>
            <th style={{ width: 36 }} />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={l.id}>
              <td>
                <input
                  data-cell
                  tabIndex={-1}
                  className="input"
                  aria-label={`รายการ แถว ${i + 1}`}
                  value={l.description}
                  disabled={readOnly}
                  onChange={(e) => patch(i, { description: e.target.value })}
                  style={{ width: '100%' }}
                />
              </td>
              <td>
                <AmountInput
                  ariaLabel={`จำนวน แถว ${i + 1}`}
                  value={l.qty}
                  disabled={!!readOnly}
                  onChange={(raw) => patch(i, { qty: raw })}
                  dataCell
                  tabIndex={-1}
                />
              </td>
              <td>
                <AmountInput
                  ariaLabel={`ราคาต่อหน่วย แถว ${i + 1}`}
                  value={l.unitPrice}
                  disabled={!!readOnly}
                  onChange={(raw) => patch(i, { unitPrice: raw })}
                  dataCell
                  tabIndex={-1}
                />
              </td>
              <td>
                <SearchSelect
                  value={l.expenseCode}
                  onChange={(code) => patch(i, { expenseCode: code })}
                  options={expenseOptions}
                  getLabel={labelFor}
                  placeholder="เลือกบัญชี"
                  disabled={!!readOnly}
                  dataCell
                  tabIndex={-1}
                />
              </td>
              <td>
                {!readOnly && (
                  <button
                    type="button"
                    className="btn sm ghost"
                    aria-label="ลบบรรทัด"
                    onClick={() => removeRow(i)}
                  >
                    ✕
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className={sumBad ? 'li-foot--bad' : undefined}>
            <td colSpan={2}>ผลรวมบรรทัด</td>
            <td className="tnum" data-testid="li-line-sum" style={{ textAlign: 'right' }}>
              {money(lineSum)}
            </td>
            <td colSpan={2}>{sumBad ? `≠ ยอดก่อน VAT ${money(subtotal)}` : ''}</td>
          </tr>
          <tr>
            <td colSpan={2}>ยอดก่อน VAT</td>
            <td className="tnum" style={{ textAlign: 'right' }}>
              {money(subtotal)}
            </td>
            <td colSpan={2} />
          </tr>
          <tr className={vatBad ? 'li-foot--bad' : undefined}>
            <td colSpan={2}>VAT 7%</td>
            <td className="tnum" style={{ textAlign: 'right' }}>
              {money(vatAmount)}
            </td>
            <td colSpan={2}>{vatBad ? `≠ 7% (${money(computedVat)})` : ''}</td>
          </tr>
          <tr className={totalBad ? 'li-foot--bad' : undefined}>
            <td colSpan={2}>ยอดรวม</td>
            <td className="tnum" style={{ textAlign: 'right' }}>
              {money(total)}
            </td>
            <td colSpan={2}>{totalBad ? '≠ ยอดก่อน VAT + VAT' : ''}</td>
          </tr>
        </tfoot>
      </table>
      {!readOnly && (
        <button
          type="button"
          className="btn sm ghost"
          aria-label="เพิ่มบรรทัด"
          style={{ marginTop: 8 }}
          onClick={addRow}
        >
          ＋ เพิ่มบรรทัด
        </button>
      )}
    </div>
  );
}
